import isEmpty from "lodash/isEmpty";

import { formatPhoneNumberToIntlAcceptedFormat } from "../../Common/utils/common";

export interface WebSignupCheckoutSession {
  userId: string;
  clientSecret: string | null;
  /** Subscription id on the membership flow, invoice id on the one-off flow. */
  paymentReferenceId: string;
}

export interface WebSignupCheckoutSessionInput {
  emailAddress: string;
  phoneNumber: string;
  priceId: string;
  couponId: string;
  promoCode: string;
  alternativePriceVersion?: string;
  referralBonusLookUpKey?: string | null;
}

/**
 * Store accessors, so the cache stays page-agnostic — each web sign-up page owns
 * its own Zustand store and must not read another page's store directly.
 */
export interface WebSignupCheckoutSessionStore {
  getCheckoutSessionKey: () => string | null;
  setCheckoutSessionKey: (value: string | null) => void;
  getUserId: () => string;
  setUserId: (value: string) => void;
  getClientSecret: () => undefined | null | string;
  setClientSecret: (value: undefined | null | string) => void;
  getPaymentReferenceId: () => undefined | null | string;
  setPaymentReferenceId: (value: undefined | null | string) => void;
}

export interface WebSignupCheckoutSessionCache {
  buildKey: (input: WebSignupCheckoutSessionInput) => string;
  get: (checkoutSessionKey: string) => WebSignupCheckoutSession | null;
  save: (params: {
    checkoutSessionKey: string;
    session: WebSignupCheckoutSession;
  }) => void;
  clear: () => void;
}

/**
 * Identity of a checkout attempt. Any change the user makes to email, phone,
 * plan, promo, price-discovery version, or referral bonus produces a different
 * key, so only a genuine repeat submit of the same input reuses the cached session.
 */
export const buildWebSignupCheckoutSessionKey = (
  input: WebSignupCheckoutSessionInput,
): string => {
  const {
    emailAddress,
    phoneNumber,
    priceId,
    couponId,
    promoCode,
    alternativePriceVersion = "",
    referralBonusLookUpKey = "",
  } = input;

  const normalizedPhoneNumber = formatPhoneNumberToIntlAcceptedFormat(phoneNumber)
    .trim()
    .replace(/\s+/g, "");

  return [
    emailAddress.toLowerCase().trim(),
    normalizedPhoneNumber,
    priceId,
    couponId,
    promoCode.toUpperCase().trim(),
    alternativePriceVersion,
    (referralBonusLookUpKey ?? "").trim(),
  ].join("|");
};

/**
 * Client-side idempotency for web sign-up checkout creation. A repeat submit of
 * identical input confirms the payment already created instead of asking the
 * backend for a second account and subscription.
 */
export const createWebSignupCheckoutSessionCache = (
  store: WebSignupCheckoutSessionStore,
): WebSignupCheckoutSessionCache => ({
  buildKey: buildWebSignupCheckoutSessionKey,

  get: (checkoutSessionKey) => {
    if (store.getCheckoutSessionKey() !== checkoutSessionKey) {
      return null;
    }

    const userId = store.getUserId();
    const clientSecret = store.getClientSecret();
    const paymentReferenceId = store.getPaymentReferenceId();

    // null is a stored already-paid leftover. lodash isEmpty(null) is true,
    // so treating null like a missing secret would miss the cache and
    // recreate signup for an email that already exists.
    if (
      isEmpty(userId) ||
      isEmpty(paymentReferenceId) ||
      (clientSecret !== null && isEmpty(clientSecret))
    ) {
      return null;
    }

    return {
      userId,
      clientSecret,
      paymentReferenceId: paymentReferenceId as string,
    };
  },

  save: ({ checkoutSessionKey, session }) => {
    store.setUserId(session.userId);
    store.setClientSecret(session.clientSecret);
    store.setPaymentReferenceId(session.paymentReferenceId);
    store.setCheckoutSessionKey(checkoutSessionKey);
  },

  clear: () => {
    store.setClientSecret(undefined);
    store.setPaymentReferenceId(undefined);
    store.setCheckoutSessionKey(null);
  },
});
